/**
 * Cross-subdomain "this browser is signed in" hint.
 *
 * WHY THIS EXISTS
 * ---------------
 * The session lives in `localStorage`, and `localStorage` is scoped to an
 * origin: `app.lydertalk.com` and `lydertalk.com` are separate stores. The
 * marketing site therefore cannot read the app's session, so a signed-in visitor
 * arriving at the landing page looked exactly like a first-time one and was left
 * on the marketing page instead of being forwarded into the app.
 *
 * The hint is a cookie on the parent domain instead — deliberately **not** the
 * token. It says "a session exists on this browser" and nothing else: no user
 * id, no credential, nothing worth stealing if the marketing origin (or a
 * third-party script on it) reads it. It is a *hint*, never an authority — the
 * app's own `localStorage` session is still the only thing that decides who is
 * signed in, so the worst a stale hint can do is cost one bounce into the app.
 *
 * Only the app writes it: `apps/app/src/main.tsx` calls `activateSessionHint()`
 * before the first render. The marketing site renders the same auth client, and
 * a sign-in there must not claim a session the app does not have.
 *
 * The cookie lives on `getCookieDomain()`, which is the deployment's base domain
 * — `lydertalk.com` in production, `stg.lydertalk.com` on staging — so a staging
 * session never publishes a hint that the production landing page reads. That
 * scoping comes from `getBaseDomain()`, which is where the `stg` / PR-preview
 * host topology is already parsed; nothing here needs to know about it.
 */

import Cookies from "js-cookie";
import { getCookieDomain } from "./subdomain";

export const SESSION_HINT_COOKIE = "lyder-talk-session";

/**
 * Seven days, refreshed on every session change and once per app boot.
 *
 * Long enough that a regular visitor is forwarded on their first load of the
 * landing page, short enough that a browser which has abandoned the app stops
 * being treated as signed in without anyone clearing anything by hand. It is
 * never the thing that ends a session — the refresh token lives 30 days, and a
 * sign-out clears the hint explicitly.
 */
const HINT_MAX_AGE_DAYS = 7;

/** Only an app that opted in publishes the hint. See `activateSessionHint`. */
let active = false;

/**
 * Opt this origin in to writing the hint. Called once by the app, before React
 * mounts, so the marketing site (which shares this module) never writes it.
 */
export function activateSessionHint(): void {
  active = true;
}

/**
 * Publish or retract the hint to match the session this origin actually holds.
 *
 * Called from `auth-client` on every session change (sign-in, sign-up, token
 * refresh, sign-out) and once at app boot. A no-op unless the origin activated
 * the hint.
 */
export function syncSessionHint(signedIn: boolean): void {
  if (!active || typeof document === "undefined") return;

  try {
    if (signedIn) {
      Cookies.set(SESSION_HINT_COOKIE, "1", cookieAttributes());
      return;
    }

    // Remove by name first, then per domain: js-cookie matches the `Domain`
    // attribute exactly, so a cookie written with `.lydertalk.com` survives a
    // removal that omits it (and vice versa).
    Cookies.remove(SESSION_HINT_COOKIE, { path: "/" });
    const domain = sharedCookieDomain();
    if (domain) {
      Cookies.remove(SESSION_HINT_COOKIE, { path: "/", domain });
      if (domain.startsWith(".")) {
        Cookies.remove(SESSION_HINT_COOKIE, { path: "/", domain: domain.slice(1) });
      }
    }
  } catch {
    // Cookies can be unavailable (privacy mode, blocked storage). The hint is an
    // optimisation for the marketing site; auth never depends on it.
  }
}

/**
 * Does this browser claim a session on the app subdomain?
 *
 * Read-only and safe to call from any origin — that is its whole purpose.
 */
export function hasSessionHint(): boolean {
  if (typeof document === "undefined") return false;

  try {
    return Cookies.get(SESSION_HINT_COOKIE) === "1";
  } catch {
    return false;
  }
}

/**
 * The domain to share the hint on, or `undefined` for a host-scoped cookie.
 *
 * On `localhost` the attribute is both unnecessary — cookies ignore the port, so
 * `:3000` (www) and `:3001` (app) already share them — and rejected by some
 * browsers, which is why `getCookieDomain`'s `.localhost` is dropped here.
 */
function sharedCookieDomain(): string | undefined {
  const domain = getCookieDomain();
  if (!domain || domain === ".localhost") return undefined;
  return domain;
}

function cookieAttributes(): Cookies.CookieAttributes {
  const attributes: Cookies.CookieAttributes = {
    path: "/",
    // Top-level navigations still carry it; the app and the marketing site are
    // same-site, so `Lax` loses nothing and keeps the cookie off cross-site
    // requests.
    sameSite: "lax",
    expires: HINT_MAX_AGE_DAYS,
  };

  if (typeof window !== "undefined" && window.location.protocol === "https:") {
    attributes.secure = true;
  }

  const domain = sharedCookieDomain();
  if (domain) attributes.domain = domain;

  return attributes;
}
